import { FC } from 'react';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { TOrderDetailProps } from '../types';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { EasCard } from '../../../../ui/EasCard';

export const DeliveryInformationCard: FC<{
	order: TOrderDetailProps['order'];
	currency: string;
}> = ({ order, currency }) => {
	if (!order) return null;

	return (
		<EasCard
			id="delivery-information-card"
			title="Delivery Information"
			action={<></>}
			loading={false}
		>
			<Typography sx={{ fontSize: 14 }} color="primary" gutterBottom>
				Delivery Information
			</Typography>
			<Typography sx={{ mt: 1.5 }} color="text.secondary">
				Recipient:
			</Typography>
			<Typography variant="h6" component="div">
				{[order.recipient_title, order.recipient_first_name, order.recipient_last_name]
					.filter(Boolean)
					.join(' ') || '—'}
			</Typography>
			{order.recipient_company_name && (
				<>
					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Company name:
					</Typography>
					<Typography>{order.recipient_company_name}</Typography>
				</>
			)}
			{order.recipient_company_vat && (
				<>
					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Company VAT number:
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
						<Typography>{order.recipient_company_vat}</Typography>
						{order.recipient_company_vat_validated && (
							<Chip
								label={order.recipient_company_vat_validated.replace('_', ' ')}
								color={
									order.recipient_company_vat_validated === 'not_validated' ? 'warning' : 'success'
								}
								size="small"
								sx={{
									p: '0 5px',
									ml: 1,
									fontSize: '.7rem',
									height: '20px',
								}}
							/>
						)}
					</Box>
				</>
			)}
			<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
				Delivery Country:
			</Typography>
			<CountryWithFlag countryCode={order.delivery_country_code} />

			<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
				Delivery Address:
			</Typography>
			<Typography>
				{[
					order.delivery_address_line_1,
					order.delivery_address_line_2,
					order.delivery_city,
					order.delivery_state_province,
					order.delivery_postal_code,
				]
					.filter(Boolean)
					.join(', ')}
			</Typography>

			<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
				Delivery to a Person:
			</Typography>
			<Typography>{order.is_delivery_to_person ? 'Yes' : 'No'}</Typography>

			{order.delivery_method && (
				<>
					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Method of Delivery:
					</Typography>
					<Typography>{order.delivery_method}</Typography>
				</>
			)}

			<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
				Delivery Cost:
			</Typography>
			<AmountAndCurrency amount={order.delivery_cost ?? 0} currency={currency} size={1} />

			{order.delivery_phone && (
				<>
					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Phone:
					</Typography>
					<Typography>{order.delivery_phone}</Typography>
				</>
			)}
			{order.delivery_email && (
				<>
					<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
						Email:
					</Typography>
					<Typography>{order.delivery_email}</Typography>
				</>
			)}
		</EasCard>
	);
};
